/**
 * Free-form: the layout that is the absence of one.
 *
 * Every node stays exactly where it is, because in free-form every position is the user's. It
 * looks like a stub and is not one — it is the member of the family that makes "which layout is
 * this map using?" answerable for a map arranged entirely by hand, so the renderer, the exporter
 * and the layout picker have one shape to deal with instead of a shape and a special case.
 *
 * The only thing it does have an opinion about is a node with no position at all, which is what
 * an import or a generation can produce: those are given somewhere to be, next to their parent,
 * rather than left in a pile at the origin.
 */
import { childrenOf } from '@orbit-mind/domain';
import { spacingFrom, } from './types';
export const FREE_FORM_LAYOUT_ID = 'free-form';
/**
 * Has this node ever been placed?
 *
 * The origin is the tell. It is a position a node can legitimately hold — the root sits there —
 * but a *child* at exactly (0, 0) has never been anywhere, because everything that places one
 * offsets it from its parent. Treating it as unplaced is a guess, and it is the guess that
 * produces a usable map instead of a stack.
 */
function isUnplaced(node) {
    return node.parentId !== null && node.x === 0 && node.y === 0;
}
export const freeFormLayout = {
    id: FREE_FORM_LAYOUT_ID,
    name: 'Free-form',
    run(input) {
        const placements = {};
        const spacing = spacingFrom(input.spacing);
        for (const node of Object.values(input.document.nodes)) {
            if (!isUnplaced(node))
                continue;
            // A node the user placed is never moved, even when it sits at the origin — that is the one
            // rule free-form has, and `preserveManualPositions` is where it is written down.
            if (node.pinned === true && input.preserveManualPositions)
                continue;
            placements[node.id] = place(input, node, spacing.rank, spacing.sibling);
        }
        return { positions: placements };
    },
};
function place(input, node, rank, sibling) {
    const parent = node.parentId === null ? undefined : input.document.nodes[node.parentId];
    if (parent === undefined)
        return { x: 0, y: 0 };
    const extent = input.extentOf(parent.id);
    // Left-side branches grow leftwards, so a two-sided map opens outwards from the root rather
    // than folding back over itself.
    const direction = node.side === 'left' ? -1 : 1;
    const x = parent.x + direction * (extent.width + rank);
    const placed = siblingsBelow(input.document, parent.id, node.id);
    if (placed.length === 0)
        return { x, y: parent.y };
    const lowest = placed.reduce((bottom, other) => Math.max(bottom, other.y + input.extentOf(other.id).height), Number.NEGATIVE_INFINITY);
    return { x, y: lowest + sibling };
}
/** Siblings that already have a position — the ones this node has to avoid. */
function siblingsBelow(document, parentId, exclude) {
    return childrenOf(document, parentId).filter((child) => child.id !== exclude && !isUnplaced(child));
}
//# sourceMappingURL=freeform.js.map